import React, { ReactNode, useEffect, useState } from 'react';
import { Drawer, Box, Typography, Button, IconButton, CircularProgress, Stack, Chip, Tooltip } from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { Buttons, FormGrid, LoadingIndicator, MoreActionsMenuButton, OccupyFreeSpace, useCopyText, useWindowHotkey } from '../primitives';
import { FormControlsForFields, useFormats } from '../schemed';
import { Schema } from '../../hooks/useSchema';
import { DescribedAddressList } from './Elements/DescribedAddressList';
import { ExtraProps } from '../schemed/FormControlProps';
import { FormattedMessage } from 'react-intl';
import { AttributesWrapper, SmallAddButton } from './Elements/Primitives';
import { ContactOrgLinkPayload } from './data/useOrganizationLink';
import { ContactOrgLinkDialog } from './Elements/ContactOrgLinkDialog';
import { ContactsListData } from './data/useContactsList';
import { ContactTags } from './Elements/ContactTags';
import { OrgLinkChip } from './Elements/OrgLinkChip';
import { AutoSuggestMultiple } from '../primitives/AutoSuggests';
import { CRMGeoSuggestionsKeys, useEnsureGeoLoaded } from './CRMGeoContext';
import { ContactNotesSection } from './Elements/Contacts/ContactNotesSection';
import { TableDisplay } from '../schemed/TableDisplay';
import { useContactAttributesConfig } from './data/useContactAttributesConfig';
import { useCRMContext } from './CRMContext';
import { ContactFilesSectionBound } from './Elements/Contacts/ContactFilesSection';
import { CustomAttributesEditors } from './Elements/Contacts/CustomAttributes/CustomAttributesEditors';
import { TimeDisplay } from '../schemed/TimeDisplay';


export interface ContactEditDrawerProps {
  schema: Schema;
  editRecord: ContactsListData["editRecord"];
  removeRecord: ContactsListData["remove"]["run"];
}

export const ContactEditDrawer = ({ schema, editRecord, removeRecord }: ContactEditDrawerProps) => {
  const isDrawerOpen = !!editRecord?.item;
  const history = useHistory();
  useEnsureGeoLoaded();

  const copyText = useCopyText();
  const attributesConfig = useContactAttributesConfig();

  const formats = useFormats();

  const [isToggledDescriptionOn, setIsToggledDescriptionOn] = useState<boolean>(false);
  const [isToggledConsentEditOn, setIsToggledConsentEditOn] = useState<boolean>(false);
  const [isToggledNameEditOn, setIsToggledNameEditOn] = useState<boolean>(false);
  
  useEffect(() => {
    if(!editRecord.hasChanges || !isDrawerOpen) {
      setIsToggledDescriptionOn(false);
      setIsToggledConsentEditOn(false);
      // Auto-open name fields if it's a completely new record
      setIsToggledNameEditOn(!editRecord.item?._id);
    }
  }, [editRecord.hasChanges, isDrawerOpen, editRecord.item?._id]);

  const ctx = useCRMContext();

  const getTitle = () => {
    const r = editRecord?.item;
    if (!r) return "Редактирование";
    const fullName = `${r.last_name || ""} ${r.first_name || ""} ${r.middle_name || ""}`.trim();
    return fullName || "Новый контакт";
  };

  const labelBase = (label: ReactNode) => (
    <Typography variant="caption" color="textSecondary" className="attribute-label">
      {label}
    </Typography>
  );

  const label = (f: string) => labelBase(schema[f]?.label);

  const control = (f: string, options?: ExtraProps) => (
    <FormControlsForFields
      key={`${f}_control`}
      fields={[
        [f, { label: " ", ...(options || {}) }],
      ]}
      schema={schema}
      data={editRecord.item}
      onChange={(o, c) => editRecord.update(c)}
    />
  );

  useWindowHotkey("esc", () => editRecord.cancel());
  useWindowHotkey("alt+s", () => editRecord.save());

  const handleOpenNewOrgLink = () => {
    editRecord.addOrEditOrgLink.startEditing({});
  };

  const handleOpenEditOrgLink = (link: ContactOrgLinkPayload) => {
    editRecord.addOrEditOrgLink.startEditing({ ...link });
  };

  const isDoNotContact = !!editRecord.item?.do_not_contact;

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={isDrawerOpen}
      elevation={8}
      PaperProps={{
        sx: { 
          width: 600,
        }
      }}
    >
      {isDrawerOpen && (
        <Box display="flex" flexDirection="column" height="100%">
          
          {/* HEADER (Fixed) */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            px={2} 
            py={2}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {/* Clickable Title */}
            <Typography 
              variant="h6" 
              onClick={() => setIsToggledNameEditOn(prev => !prev)}
              title="Нажмите для редактирования ФИО"
              sx={{ 
                cursor: 'pointer', 
                borderBottom: '1px dashed transparent',
                transition: 'all 0.2s',
                '&:hover': { 
                  color: 'primary.main',
                  borderColor: 'primary.main' 
                } 
              }}
            >
              {getTitle()}
            </Typography>

            <OccupyFreeSpace />

            {(editRecord.isLoading || editRecord.extraInfo.isLoading) && <LoadingIndicator sizeVariant="s" />}
            {isDoNotContact && <Chip variant="filled" color="error" size="small" label="не контактировать" />}

            <MoreActionsMenuButton
              actions={[
                !isDoNotContact ? ["do_not_contact", "Не контактировать", () => editRecord.update({ do_not_contact: true })] : null,
                !!isDoNotContact ? ["do_not_contact", "Можно контактировать", () => editRecord.update({ do_not_contact: false })] : null,
                ["copyid", <FormattedMessage id="userManagement.copy_id" />, () => editRecord.item && copyText(editRecord.item._id || "")],
                ["remove", <FormattedMessage id="common.delete" />, () => editRecord.item && removeRecord(editRecord.item)],
              ]}
              />
            <IconButton size="small" onClick={() => editRecord.cancel()}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* SCROLLABLE CONTENT AREA */}
          <Box flex={1} overflow="auto" px={2} py={2}>
            <AttributesWrapper columns="150px 2fr">
              
              {/* Conditionally rendered Name Fields */}
              {isToggledNameEditOn && (
                <React.Fragment>
                  {label("last_name")}
                  {control("last_name", { controlProps: { autoFocus: true }})}

                  {label("first_name")}
                  {control("first_name")}

                  {label("middle_name")}
                  {control("middle_name")}
                </React.Fragment>
              )}
              
              
              {!!ctx.settings.contact_categories?.length && <>
                {label("categories")}
                {control("categories", { autoComplete: true })}
              </>}

              {label("tags")}
              <Box sx={{ minWidth: 0 }}> 
                <ContactTags 
                  tagIds={editRecord.item?.tags} 
                  onUpdateTags={(newIds) => editRecord.update({ tags: newIds as any })}
                />
              </Box>
              
              {label("geo_country")}
              {control("geo_country", { control: AutoSuggestMultiple, suggestionsKey: CRMGeoSuggestionsKeys.countries })}

              {label("geo_city")}
              {control("geo_city", { control: AutoSuggestMultiple, suggestionsKey: CRMGeoSuggestionsKeys.cities })}

              {/* Organizations Area */}
              {label("organization_name")}
              <Box sx={{ minWidth: 0 }}>
                {editRecord.extraInfo.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Stack spacing={1} mb={1} useFlexGap direction="row" flexWrap="wrap">
                    {(editRecord.extraInfo.data?.organizations || []).map((orgLink: any, idx: number) => (
                      <OrgLinkChip
                        key={orgLink._id || idx}
                        organizationName={orgLink.organization_name}
                        positionTitle={orgLink.title} 
                        isActive={orgLink.is_active !== false}
                        onClick={(e) => {
                          const url = `/org/crm/organization?id=${orgLink.organization_id}`;
                          if (e.ctrlKey || e.metaKey) {
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } else {
                            history.push(url);
                          }
                        }}
                        onLinkClick={() => handleOpenEditOrgLink(orgLink)}
                      />
                    ))}
                
                  <SmallAddButton iconOnly={!!editRecord.extraInfo.data?.organizations?.length} icon={<Add />} onClick={handleOpenNewOrgLink}>
                    добавить компанию
                  </SmallAddButton>
                  </Stack>
                )}
              </Box>

              <Typography variant="caption" color="textSecondary" className="attribute-label">
                Коммуникации
              </Typography>

              {isToggledConsentEditOn
                ? (
                  <FormGrid columns="1fr 1fr" gap="dense" noMargin>
                    <Typography variant="caption" color="textSecondary">Согласие ПД</Typography>
                    {control("consent_pd")}
                    <Typography variant="caption" color="textSecondary">Согласие рассылки</Typography>
                    {control("consent_emails")}
                    <Typography variant="caption" color="textSecondary">Язык коммуникации</Typography>
                    {control("communication_language", { autoComplete: true })}
                  </FormGrid>
                )
                : (
                <Box sx={{ cursor: "pointer" }} onClick={() => setIsToggledConsentEditOn(true)}>
                  <Typography variant="caption">
                    согласие ПД:{" "}
                    <Typography variant="caption" sx={{ color: editRecord.item?.consent_pd === "yes" ? "success.main" : "error.main" }}><TableDisplay field="consent_pd" row={editRecord.item} schema={schema.consent_pd} /></Typography>
                    , рассылки:{" "}
                    <Typography variant="caption" sx={{ color: editRecord.item?.consent_emails === "yes" ? "success.main" : "error.main" }}><TableDisplay field="consent_emails" row={editRecord.item} schema={schema.consent_emails} /> </Typography>
                    , язык:{" "}
                    <Typography variant="caption" sx={{ color: "success.main" }}><TableDisplay field="communication_language" row={editRecord.item} schema={schema.communication_language} /> </Typography>
                  </Typography>
                </Box>
                )}

              {label("emails")}
              <DescribedAddressList
                type="email"
                items={editRecord.item?.emails}
                onChange={(newEmails) => editRecord.update({ emails: newEmails })}
              />

              {label("phones")}
              <DescribedAddressList
                type="phone"
                items={editRecord.item?.phones}
                onChange={(newPhones) => editRecord.update({ phones: newPhones })}
              />

              <Typography key={`birth_date_gender_label`} variant="caption" color="textSecondary" className="attribute-label">
                Дата рождения, Пол
              </Typography>
              <FormGrid columns="1fr 100px" noMargin>
                {control("birth_date")}
                {control("gender", { autoComplete: true })}
              </FormGrid>

              {/*  */}
              {/* CUSTOM ATTRIBUTES */}
              {/*  */}
              <CustomAttributesEditors 
                editRecord={editRecord} 
                attributesConfig={attributesConfig} 
              />

              {label("description")}
              {isToggledDescriptionOn || !!editRecord.item?.description?.length
                ? control("description", isToggledDescriptionOn ? { controlProps: { autoFocus: true }} : undefined)
                : <Buttons>
                  <SmallAddButton icon={<Add />} onClick={() => setIsToggledDescriptionOn(true)}>
                    добавить описание
                  </SmallAddButton>
                </Buttons>}

              <Typography variant="caption" color="textSecondary" className="attribute-label">
                Файлы
              </Typography>
              <ContactFilesSectionBound
                key={editRecord.item?._id || "x"}
                contactId={editRecord.item?._id}
                />

              <Typography variant="caption" color="textSecondary" className="attribute-label">
                Заметки
              </Typography>
              <ContactNotesSection
                data={editRecord.notes}
              />

              <div></div>

              <Typography variant="caption" color="textSecondary" sx={{ userSelect: "none" }}>
                Контакт создан{" "}
                <Tooltip title={
                  <>
                  <TimeDisplay 
                    field="created_at"
                    row={editRecord.item}
                    schema={schema}
                    config={{ utcToLocal: true }}
                  />

                  {" "}
                  <TableDisplay
                    field="created_by"
                    row={editRecord.item}
                    schema={schema.created_by}
                    />
                </>}>
                  <span>
                    <TimeDisplay
                      field="created_at"
                      row={editRecord.item}
                      schema={schema}
                      config={{ utcToLocal: true, format: formats.date }}
                    />
                  </span>
                </Tooltip>,
                обновлен{" "}
                <Tooltip title={
                  <>
                  <TimeDisplay 
                    field="updated_at"
                    row={editRecord.item}
                    schema={schema}
                    config={{ utcToLocal: true }}
                  />

                  {" "}
                  <TableDisplay
                    field="updated_by"
                    row={editRecord.item}
                    schema={schema.updated_by}
                    />
                </>}>
                  <span>
                    <TimeDisplay
                      field="updated_at"
                      row={editRecord.item}
                      schema={schema}
                      config={{ utcToLocal: true, format: formats.date }}
                    />
                  </span>
                </Tooltip>
              </Typography>
            </AttributesWrapper>
          </Box>

          {/* FOOTER (Fixed) */}
          <Box 
            display="flex" 
            justifyContent="flex-end" 
            gap={1} 
            px={2} 
            py={2}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Button size="small" onClick={() => editRecord.cancel()}><FormattedMessage id="common.close" /></Button>
            {editRecord.hasChanges &&
              <Button variant="contained" color="primary" onClick={() => editRecord.save()}>Сохранить</Button>}
          </Box>

        </Box>
      )}

      {/* Reusable Dialog for linking organizations */}
      {editRecord.addOrEditOrgLink.isEditing && (
        <ContactOrgLinkDialog
          data={editRecord.addOrEditOrgLink}
          onDelete={x => editRecord.removeOrgLink.run(x as any)}
        />
      )}
    </Drawer>
  );
};